import React, { useState, useRef } from 'react';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faImage, faPaperPlane, faSpinner } from '@/icons';
import { ScreenshotResult } from '../services/ScreenshotService';
import { CustomTooltip } from './CustomTooltip';

export interface HelpModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSend: (message: string, includeScreenshot: boolean, screenshot?: ScreenshotResult) => Promise<void>;
    screenshot?: ScreenshotResult;
    isSending?: boolean;
    isCapturingScreenshot?: boolean;
}

/**
 * Modal component for submitting help requests with optional screenshot
 */
export const HelpModal: React.FC<HelpModalProps> = ({
    isOpen,
    onClose,
    onSend,
    screenshot,
    isSending = false,
    isCapturingScreenshot = false
}) => {
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);
    const mouseDownOutsideRef = useRef<boolean>(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [charCountUpdate, setCharCountUpdate] = useState(0); // Used only to force re-render, not to store count

    const [message, setMessage] = useState('');
    const [includeScreenshot, setIncludeScreenshot] = useState(true);
    const [showFullSizeScreenshot, setShowFullSizeScreenshot] = useState(false);

    const MIN_MESSAGE_LENGTH = 10;
    const MAX_MESSAGE_LENGTH = 5000;

    // Calculate character count directly from textarea value (not from state)
    const getCharacterCount = (): number => {
        if (textareaRef.current) {
            return textareaRef.current.value.length;
        }
        return message.length; // Fallback to state if ref not available
    };

    const currentLength = getCharacterCount();
    const isValidLength = currentLength >= MIN_MESSAGE_LENGTH && currentLength <= MAX_MESSAGE_LENGTH;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const trimmedMessage = message.trim();
        if (!trimmedMessage || trimmedMessage.length < MIN_MESSAGE_LENGTH || trimmedMessage.length > MAX_MESSAGE_LENGTH) {
            return;
        }

        await onSend(trimmedMessage, includeScreenshot, includeScreenshot ? screenshot : undefined);
        setMessage('');
        setIncludeScreenshot(true);
    };

    const handleClose = () => {
        setMessage('');
        setIncludeScreenshot(true);
        setShowFullSizeScreenshot(false);
        onClose();
    };

    if (!isOpen) return null;

    const handleMouseDown = (e: React.MouseEvent) => {
        // Track if mousedown happened on the overlay (outside modal content)
        if (e.target === e.currentTarget) {
            mouseDownOutsideRef.current = true;
        } else {
            mouseDownOutsideRef.current = false;
        }
    };

    const handleMouseUp = (e: React.MouseEvent) => {
        // Only close if both mousedown and mouseup happened outside the modal
        if (e.target === e.currentTarget && mouseDownOutsideRef.current) {
            handleClose();
        }
        // Reset the flag
        mouseDownOutsideRef.current = false;
    };

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: theme.spacing.lg,
        }} onMouseDown={handleMouseDown} onMouseUp={handleMouseUp}>
            <div style={{
                backgroundColor: theme.colors.surface,
                borderRadius: theme.borderRadius.xl,
                boxShadow: theme.shadows.lg,
                width: '100%',
                maxWidth: '600px',
                maxHeight: '90vh',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
            }} onMouseDown={(e) => e.stopPropagation()} onMouseUp={(e) => e.stopPropagation()}>
                {/* Header */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: theme.spacing.lg,
                    borderBottom: `1px solid ${theme.colors.border.default}`,
                }}>
                    <h2 style={{
                        margin: 0,
                        fontSize: theme.typography.fontSize.xl,
                        fontWeight: theme.typography.fontWeight.semibold,
                        color: theme.colors.text.primary,
                    }}>
                        Need Help?
                    </h2>
                    <button
                        onClick={handleClose}
                        disabled={isSending}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: isSending ? 'not-allowed' : 'pointer',
                            padding: theme.spacing.sm,
                            borderRadius: theme.borderRadius.sm,
                            color: theme.colors.text.secondary,
                            opacity: isSending ? 0.5 : 1,
                        }}
                        aria-label="Close modal"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {/* Content */}
                <div style={{
                    padding: theme.spacing.lg,
                    flex: 1,
                    overflow: 'auto',
                }}>
                    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.lg }}>
                        {/* Screenshot Preview */}
                        {(screenshot || isCapturingScreenshot) && (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: theme.spacing.sm,
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: theme.spacing.sm,
                                }}>
                                    <FontAwesomeIcon
                                        icon={isCapturingScreenshot ? faSpinner : faImage}
                                        color={theme.colors.primary}
                                        spin={isCapturingScreenshot}
                                    />
                                    <span style={{
                                        fontSize: theme.typography.fontSize.sm,
                                        fontWeight: theme.typography.fontWeight.medium,
                                        color: theme.colors.text.primary,
                                    }}>
                                        {isCapturingScreenshot ? 'Capturing screenshot...' :
                                            includeScreenshot ? 'Screenshot Captured' : 'Screenshot Captured (Not Shared)'}
                                    </span>
                                </div>

                                <CustomTooltip text={isCapturingScreenshot ? undefined : (includeScreenshot ? "Click to view full size" : undefined)}>
                                <div style={{
                                    position: 'relative',
                                    border: `1px solid ${theme.colors.border.default}`,
                                    borderRadius: theme.borderRadius.md,
                                    overflow: 'hidden',
                                    backgroundColor: theme.colors.surfaceSecondary,
                                    cursor: (isCapturingScreenshot || !includeScreenshot) ? 'default' : 'pointer',
                                    transition: `border-color ${theme.transitions.normal}`,
                                    minHeight: '200px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                                    onClick={(!isCapturingScreenshot && includeScreenshot) ? () => setShowFullSizeScreenshot(true) : undefined}
                                    onMouseEnter={(e) => {
                                        if (!isCapturingScreenshot && includeScreenshot) {
                                            e.currentTarget.style.borderColor = theme.colors.primary;
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isCapturingScreenshot && includeScreenshot) {
                                            e.currentTarget.style.borderColor = theme.colors.border.default;
                                        }
                                    }}
                                >
                                    {isCapturingScreenshot ? (
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: theme.spacing.md,
                                            color: theme.colors.text.secondary,
                                        }}>
                                            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                                            <span style={{
                                                fontSize: theme.typography.fontSize.sm,
                                                fontWeight: theme.typography.fontWeight.medium,
                                            }}>
                                                Capturing screenshot...
                                            </span>
                                        </div>
                                    ) : !includeScreenshot ? (
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: theme.spacing.md,
                                            color: theme.colors.text.secondary,
                                        }}>
                                            <FontAwesomeIcon icon={faImage} size="2x" />
                                            <span style={{
                                                fontSize: theme.typography.fontSize.sm,
                                                fontWeight: theme.typography.fontWeight.medium,
                                                textAlign: 'center',
                                            }}>
                                                Screenshot will not be shared
                                            </span>
                                        </div>
                                    ) : screenshot ? (
                                        <img
                                            src={screenshot.dataUrl}
                                            alt="Screenshot"
                                            style={{
                                                width: '100%',
                                                height: 'auto',
                                                maxHeight: '200px',
                                                objectFit: 'contain',
                                                display: 'block',
                                            }}
                                        />
                                    ) : null}
                                </div>
                                </CustomTooltip>

                                {/* Include Screenshot Toggle */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: theme.spacing.sm,
                                }}>
                                    <div
                                        style={{
                                            position: 'relative',
                                            width: '40px',
                                            height: '22px',
                                            backgroundColor: includeScreenshot ? theme.colors.primary : theme.colors.border.default,
                                            borderRadius: '11px',
                                            transition: 'background-color 0.2s ease',
                                            cursor: (isSending || isCapturingScreenshot) ? 'not-allowed' : 'pointer',
                                            opacity: (isSending || isCapturingScreenshot) ? 0.6 : 1,
                                        }}
                                        onClick={() => !isSending && !isCapturingScreenshot && setIncludeScreenshot(!includeScreenshot)}
                                    >
                                        <div
                                            style={{
                                                position: 'absolute',
                                                top: '2px',
                                                left: includeScreenshot ? '20px' : '2px',
                                                width: '18px',
                                                height: '18px',
                                                backgroundColor: 'white',
                                                borderRadius: '50%',
                                                transition: 'left 0.2s ease',
                                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                                            }}
                                        />
                                    </div>
                                    <span style={{
                                        fontSize: theme.typography.fontSize.sm,
                                        color: theme.colors.text.secondary,
                                        userSelect: 'none',
                                    }}>
                                        Include screenshot with help request
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Message Input */}
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: theme.spacing.sm,
                        }}>
                            <label htmlFor="help-message" style={{
                                fontSize: theme.typography.fontSize.sm,
                                fontWeight: theme.typography.fontWeight.medium,
                                color: theme.colors.text.primary,
                            }}>
                                Describe your issue *
                            </label>
                            <textarea
                                ref={textareaRef}
                                id="help-message"
                                value={message}
                                onChange={(e) => {
                                    setMessage(e.target.value);
                                    // Force re-render to update character count display
                                    setCharCountUpdate(prev => prev + 1);
                                }}
                                onInput={(e) => {
                                    // Force re-render on each keypress for real-time updates
                                    setCharCountUpdate(prev => prev + 1);
                                }}
                                placeholder="Please describe the issue you're experiencing. Include steps to reproduce if possible..."
                                disabled={isSending}
                                required
                                maxLength={MAX_MESSAGE_LENGTH}
                                rows={6}
                                style={{
                                    width: '100%',
                                    padding: theme.spacing.md,
                                    border: `1px solid ${theme.colors.border.default}`,
                                    borderRadius: theme.borderRadius.md,
                                    fontSize: theme.typography.fontSize.md,
                                    fontFamily: theme.typography.fontFamily,
                                    resize: 'vertical',
                                    minHeight: '120px',
                                    backgroundColor: theme.colors.surface,
                                    color: theme.colors.text.primary,
                                    outline: 'none',
                                    transition: `border-color ${theme.transitions.normal}`,
                                    opacity: isSending ? 0.6 : 1,
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = theme.colors.border.focus;
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = theme.colors.border.default;
                                }}
                            />
                            <div style={{
                                display: 'flex',
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                marginTop: theme.spacing.xs,
                            }}>
                                <span style={{
                                    fontSize: theme.typography.fontSize.xs,
                                    color: isValidLength ? theme.colors.text.secondary : theme.colors.status.error,
                                    fontWeight: theme.typography.fontWeight.normal,
                                }}>
                                    {currentLength} characters (between {MIN_MESSAGE_LENGTH} and {MAX_MESSAGE_LENGTH} accepted)
                                </span>
                            </div>
                        </div>
                    </form>
                </div>

                {/* Footer */}
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: theme.spacing.sm,
                    padding: theme.spacing.lg,
                    borderTop: `1px solid ${theme.colors.border.default}`,
                    backgroundColor: theme.colors.surfaceSecondary,
                }}>
                    {/* Buttons */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: theme.spacing.md,
                    }}>
                        <button
                            type="button"
                            onClick={handleClose}
                            disabled={isSending}
                            style={{
                                backgroundColor: 'transparent',
                                color: theme.colors.text.secondary,
                                border: `1px solid ${theme.colors.border.default}`,
                                borderRadius: theme.borderRadius.lg,
                                padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
                                fontSize: theme.typography.fontSize.md,
                                fontWeight: theme.typography.fontWeight.medium,
                                cursor: isSending ? 'not-allowed' : 'pointer',
                                opacity: isSending ? 0.6 : 1,
                                transition: `all ${theme.transitions.normal}`,
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            onClick={handleSubmit}
                            disabled={isSending || !isValidLength}
                            style={{
                                ...themedStyles.button.primary,
                                display: 'flex',
                                alignItems: 'center',
                                gap: theme.spacing.sm,
                                opacity: (isSending || !isValidLength) ? 0.6 : 1,
                                cursor: (isSending || !isValidLength) ? 'not-allowed' : 'pointer',
                            }}
                        >
                            {isSending ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} spin />
                                    Sending...
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faPaperPlane} />
                                    Send Help Request
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Full-size Screenshot Viewer */}
            {showFullSizeScreenshot && screenshot && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.9)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10001,
                    padding: theme.spacing.lg,
                }}
                    onClick={() => setShowFullSizeScreenshot(false)}
                >
                    <div style={{
                        position: 'relative',
                        maxWidth: '95vw',
                        maxHeight: '95vh',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <CustomTooltip text="Click to close">
                        <img
                            src={screenshot.dataUrl}
                            alt="Screenshot - Full Size"
                            style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                                borderRadius: theme.borderRadius.md,
                                boxShadow: theme.shadows.lg,
                                cursor: 'pointer',
                            }}
                            onClick={() => setShowFullSizeScreenshot(false)}
                        />
                        </CustomTooltip>
                    </div>
                </div>
            )}
        </div>
    );
};
